<script setup>
  import {computed, ref} from "vue";

  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import {MedButton} from "@pharma4u/medicheck-components";

  defineOptions({
    name: "ComponentDosageDialog",
  });

  const emit = defineEmits(["useRecommendedValue"]);

  const refDosageDialog = ref(null);

  const id = ref(null);
  const type = ref(null);
  const errorCode = ref(null);
  const currentValue = ref(null);
  const recommendedValue = ref(null);
  const correctedValue = ref(null);

  const dosageTime = computed(() => {
    if (id.value.toString().toLowerCase().includes("morning")) {
      return "Morgen";
    } else if (id.value.toString().toLowerCase().includes("noon")) {
      return "Mittag";
    } else if (id.value.toString().toLowerCase().includes("evening")) {
      return "Abend";
    } else if (id.value.toString().toLowerCase().includes("night")) {
      return "Nacht";
    }

    return null;
  });

  const open = (event) => {
    id.value = event.id;
    type.value = event.type;
    errorCode.value = event.errorCode;
    currentValue.value = event.currentValue;
    recommendedValue.value = event.recommendedValue;
    // init correctedValue with recommendedValue
    correctedValue.value = event.recommendedValue;

    refDosageDialog.value.open();
  };

  const primaryButtonClicked = () => {
    emit("useRecommendedValue", {
      id: id.value,
      value: correctedValue.value,
    });

    refDosageDialog.value.close();
    reset();
  };

  const cancelButtonClicked = () => {
    refDosageDialog.value.close();
    reset();
  };

  const reset = () => {
    id.value = null;
    type.value = null;
    errorCode.value = null;
    currentValue.value = null;
    recommendedValue.value = null;
    correctedValue.value = null;
  };

  defineExpose({
    open,
  });
</script>

<template>
  <ComponentDialog ref="refDosageDialog">
    <template #title>
      <template v-if="type === 'info'">Hinweis zur Dosierungsangabe</template>
      <template v-else>Dosierungsangabe ungültig</template>
    </template>

    <template #content>
      <template v-if="type === 'info'">
        <p>Die Dosierung "{{ currentValue }}" kann auch als "{{ recommendedValue }}" eingegeben werden.</p>
      </template>

      <template v-else>
        <p>
          Die angegebene

          <template v-if="!dosageTime">Dosierung</template>
          <template v-else>{{ dosageTime }}-Dosierung</template>

          <!-- prettier-ignore -->
          <template v-if="currentValue">
            ("<span class="font-semibold">{{ currentValue }}</span>")
          </template>
          widerspricht der Spezifikation für einen bundeseinheitlichen Medikationsplan.
        </p>

        <template v-if="errorCode === 'valueNotSet'">
          <p>Es muss ein Wert gesetzt sein.</p>
        </template>

        <template v-if="errorCode === 'fractionError'">
          <p>Es dürfen nur die Brüche ½, ⅓, ¼, ⅔, ¾ und ⅛ eingegeben werden.</p>
        </template>

        <template v-else-if="errorCode === 'valueToLong'">
          <p>Die Dosierung darf maximal 4 Zeichen lang sein.</p>
        </template>

        <template v-else-if="errorCode === 'invalidChars'">
          <p>Es dürfen nur Zahlen, ein Komma oder ein Schrägstrich eingegeben werden.</p>
        </template>

        <template v-if="recommendedValue">
          <div class="mt-2 flex items-center space-x-2">
            <span>Stattdessen</span>
            <input v-model="correctedValue" type="text" aria-label="Korrigierte Dosierung" class="max-w-16" />
            <span>übernehmen.</span>
          </div>
        </template>
      </template>
    </template>

    <template #actions>
      <template v-if="recommendedValue">
        <MedButton
          variant="primary"
          :disabled="correctedValue === ''"
          :data-test="getTestId('dosage-dialog-apply-button')"
          @click="primaryButtonClicked"
        >
          Übernehmen
        </MedButton>
      </template>

      <template v-else>
        <MedButton
          variant="primary"
          :data-test="getTestId('dosage-dialog-edit-input-button')"
          @click="cancelButtonClicked"
        >
          Eingabe bearbeiten
        </MedButton>
      </template>
    </template>
  </ComponentDialog>
</template>
